import { useEffect, useRef, useState } from "react";
import {
  createChart,
  TickMarkType,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import { resolveCssVar } from "../../utils/cssColor";
import type { Theme } from "../../theme/useTheme";
import { formatMoney, formatMskDate, formatPct, formatPnl, formatTime, formatTradeId } from "../../utils/format";

// lightweight-charts formats its own axis ticks / crosshair time label in
// UTC by default — it has no timezone-aware mode, so this app's "every
// timestamp shown as MSK" requirement has to be done by hand for the
// chart's own labels too, not just the surrounding page. Confirmed live:
// without this, the axis/crosshair showed raw UTC while every other clock
// on the page correctly showed MSK.
function timeToDate(time: Time): Date {
  return new Date((time as UTCTimestamp) * 1000);
}

function tickMarkFormatter(time: Time, tickMarkType: TickMarkType): string {
  const d = timeToDate(time);
  if (tickMarkType === TickMarkType.Year) return String(d.getUTCFullYear());
  // getUTCFullYear() above is fine (a 4-digit year is timezone-invariant at
  // this granularity) — everything below actually needs the MSK-shifted
  // parts, done via the shared formatMskDate/formatTime helpers.
  if (tickMarkType === TickMarkType.Month || tickMarkType === TickMarkType.DayOfMonth) {
    return formatMskDate(d.toISOString());
  }
  return formatTime(d.toISOString());
}

function crosshairTimeFormatter(time: Time): string {
  return formatTime(timeToDate(time).toISOString(), true);
}

// chart.timeScale().timeToCoordinate(time) only resolves a time that
// EXACTLY matches one of the active series' own data-point timestamps — it
// does an index lookup, not a continuous time->pixel mapping, and returns
// null for anything in between (confirmed live: trade marker times rarely
// land on an exact price-sample timestamp, so most markers silently failed
// hit-testing with the direct call). A fractional-logical-index approach
// via logicalToCoordinate was tried and produced inconsistent/wrong pixel
// positions live (not investigated further — not worth it given the fix
// below is both simpler and matches what's already visually true): binary
// search for the nearest ACTUAL bar time to the marker's time, then call
// the proven-reliable exact-match timeToCoordinate on that. This snaps the
// hit-test target to the same bar the native marker arrow is itself
// visually anchored to (setMarkers() also snaps to an existing bar time),
// so hit-testing and the visible arrow agree by construction.
// Moscow has been on a fixed UTC+3 offset with no DST since 2014, so "start
// of day in MSK" is always a plain arithmetic shift, no timezone table
// needed. Returns every MSK-midnight UTC-second timestamp within
// [fromSec, toSec] — used to draw the day-boundary gridlines below.
const MSK_OFFSET_SEC = 3 * 3600;
const DAY_SEC = 86400;
function mskDayBoundaries(fromSec: number, toSec: number): number[] {
  let t = Math.floor((fromSec + MSK_OFFSET_SEC) / DAY_SEC) * DAY_SEC - MSK_OFFSET_SEC;
  if (t < fromSec) t += DAY_SEC;
  const boundaries: number[] = [];
  for (; t <= toSec; t += DAY_SEC) boundaries.push(t);
  return boundaries;
}

// `floor`: pick the bracketing bar strictly BY DISTANCE ("nearest", the
// default) or always the earlier bracketing bar ("floor"). These disagree,
// and it matters which one a caller picks: for line mode's raw per-sample
// timestamps there's no "ownership" concept, so nearest-by-distance is the
// right visual proxy for where a marker sits relative to the plotted line.
// For CANDLE mode, each bar time is a bucket START (e.g. daily bars are
// timestamped at 00:00) representing everything until the next bucket — an
// event at 13:00 on a given day belongs to THAT day's bar even though it's
// numerically closer to the following midnight (11h away) than to its own
// day's midnight (13h away). Confirmed live: a 16.08 13:00 trade's arrow/
// hover target was silently snapping to the 17.08 bar with plain nearest-
// by-distance, making it fall wherever the 17.08 cluster already was and
// look like a "hover doesn't show tooltips" dead zone right around 16.08 —
// nearbyMarkers passes floor=true for exactly this case.
function timeToCoordinateNearestBar(chart: IChartApi, times: number[], time: number, floor = false): number | null {
  if (times.length === 0) return null;
  const toCoord = (t: number) => chart.timeScale().timeToCoordinate(t as UTCTimestamp);
  if (time <= times[0]) return toCoord(times[0]);
  if (time >= times[times.length - 1]) return toCoord(times[times.length - 1]);
  let lo = 0;
  let hi = times.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= time) lo = mid;
    else hi = mid;
  }
  if (floor) return toCoord(times[lo]);
  const nearest = time - times[lo] <= times[hi] - time ? times[lo] : times[hi];
  return toCoord(nearest);
}

export type ChartMode = "line" | "candles";

export interface PriceChartPoint {
  time: UTCTimestamp; // unix seconds
  value: number;
}

export interface PriceChartCandle {
  time: UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface PriceChartMarker {
  time: UTCTimestamp;
  tradeId: string;
  botLabel: string | null;
  kind: "entry" | "exit";
  direction: "LONG" | "SHORT";
  price: number; // this marker's own point (chart position)
  entryPrice: number;
  exitPrice: number | null;
  entryTime: UTCTimestamp;
  exitTime: UTCTimestamp | null;
  size: string;
  pnl: string | null; // formatted (money()) string, null while still OPEN
  status: "OPEN" | "CLOSED";
  stopLoss: number | null;
  takeProfit: number | null;
}

export interface PriceChartLevel {
  price: number;
  kind: "entry" | "sl" | "tp";
  title: string;
}

interface PriceChartProps {
  mode: ChartMode;
  data: PriceChartPoint[];
  candles?: PriceChartCandle[];
  markers?: PriceChartMarker[];
  levels?: PriceChartLevel[];
  theme: Theme;
  height?: number;
  // Disables user-driven pan/zoom (mouse wheel, drag, pinch) entirely —
  // Overview.tsx's own period-tab + ←/→ nav arrows replaced free scrolling
  // as the only way to move through history (user request: "график не
  // поддерживает скролл за пределы выбранного отрезка времени, вместо
  // скролла будут эти стрелки навигации"). With no user-driven pan to
  // preserve, the data-update effect below always fits the full current
  // window instead of the old "was this a range switch or just a poll
  // refresh" guesswork that needed (see git history — a real bug that
  // guesswork produced: a SECOND switch back to an already-visited range
  // silently kept the previous range's view).
  disablePan?: boolean;
  onMarkerClick?: (tradeId: string) => void;
  // Fires once per chart (re)creation with the raw IChartApi — lets a
  // parent wire two-way timeScale sync with another chart (see
  // PnlChart.tsx / Overview.tsx), without this component knowing that
  // chart exists.
  onChartApi?: (chart: IChartApi) => void;
}

// Pixel radius for "is the cursor over this marker" hit-testing — see the
// crosshair-move/click handlers below. Deliberately generous: the arrow
// glyph itself is rendered "aboveBar"/"belowBar" by the library with its
// own internal padding beyond the bar's actual price coordinate, which
// isn't queryable — a tight radius around the raw priceToCoordinate(m.price)
// point undershoots the visible arrow. Confirmed by hovering the actual
// rendered marker live and tuning until it reliably hit.
const HOVER_PX = 26;

// Vertical spacing lightweight-charts itself uses when several markers land
// on the SAME bar and the SAME side (aboveBar/belowBar) — confirmed live by
// pixel-measuring a dense cluster of same-day "выход" arrows: the library
// stacks them progressively further from the bar in fixed ~44px steps, not
// at each marker's own trade price. Needed for hit-testing below (see
// nearbyMarkers' own comment) since there's no public API to read back a
// marker's actual rendered position.
const MARKER_STACK_OFFSET_PX = 44;

/**
 * TradingView lightweight-charts wrapper. This is the one intentional
 * exception to idiomatic React in this project (per plan): all chart-library
 * calls (createChart/setData/setMarkers/createPriceLine) are imperative,
 * contained in a single useEffect + cleanup, and nothing about the
 * lightweight-charts API leaks past this component's props.
 *
 * Trade markers are drawn via the library's native setMarkers() for visual
 * placement, but v4 has no built-in marker hover/click API — so
 * hover-tooltip and click-to-select are implemented by hand: on every
 * crosshair-move/click, each marker's own (time, price) is converted to
 * pixel coordinates via the chart's own timeScale/series conversion, and
 * whichever marker is within HOVER_PX of the cursor wins. This is exact
 * (uses the chart's real rendering, not a guessed time threshold) and
 * works the same regardless of zoom level or how densely the underlying
 * price samples are spaced.
 */
export function PriceChart({
  mode,
  data,
  candles = [],
  markers = [],
  levels = [],
  theme,
  height = 320,
  disablePan = false,
  onMarkerClick,
  onChartApi,
}: PriceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Area"> | ISeriesApi<"Candlestick"> | null>(null);
  // Debounced delayed re-fit for candlestick mode — see the data-update
  // effect's own comment for why this needs to be a full setTimeout, not a
  // synchronous or rAF-deferred call.
  const candleRefitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markersRef = useRef<PriceChartMarker[]>(markers);
  const onMarkerClickRef = useRef(onMarkerClick);
  const onChartApiRef = useRef(onChartApi);
  // Sorted times of whichever series is currently active — feeds
  // timeToCoordinateNearestBar's bracket search (see its comment).
  const activeTimesRef = useRef<number[]>([]);
  // markers: plural, not just the closest one. market_metrics is logged
  // hourly (see overview.py's _CANDLE_BUCKET), but trades themselves can
  // open/close minutes apart — every marker in that hour snaps to the SAME
  // bar via timeToCoordinateNearestBar, so several distinct trades can
  // land on the exact same x pixel. Picking only the single closest one
  // (the old behavior) silently made the others unreachable by hover —
  // confirmed live: two real trades 5 minutes apart, hovering only ever
  // surfaced one of them. Listing every marker within HOVER_PX instead of
  // guessing which one the cursor "really" means.
  const [tooltip, setTooltip] = useState<{ x: number; y: number; markers: PriceChartMarker[] } | null>(null);
  // Day-boundary gridlines' own x pixel positions — recomputed on pan/zoom,
  // resize, and data updates (see recomputeDayLinesRef below). Kept in a
  // ref (not just state) so the resize observer and the data-update effect,
  // which run outside the effect that defines the actual computation, can
  // trigger it without depending on it in their own effect's closure.
  const [dayLineX, setDayLineX] = useState<number[]>([]);
  const recomputeDayLinesRef = useRef<() => void>(() => {});

  markersRef.current = markers;
  onMarkerClickRef.current = onMarkerClick;
  onChartApiRef.current = onChartApi;
  activeTimesRef.current = (mode === "candles" ? candles : data).map((p) => p.time as number);

  // Create/destroy the chart when the container mounts, theme changes, or
  // the line/candles mode switches (a series' type can't change in place —
  // addAreaSeries and addCandlestickSeries are different chart objects).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const fg3 = resolveCssVar("--fg3");
    const grid = resolveCssVar("--grid");
    const accent = resolveCssVar("--accent");
    const chartFill = resolveCssVar("--chartfill");
    const line = resolveCssVar("--line");
    const long = resolveCssVar("--long");
    const short = resolveCssVar("--short");

    const chart = createChart(container, {
      width: container.clientWidth,
      height,
      layout: {
        background: { color: "transparent" },
        textColor: fg3,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        // Hides the "Powered by TradingView" attribution — lightweight-charts
        // >=4.1 supports turning it off explicitly.
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: grid },
        horzLines: { color: grid },
      },
      rightPriceScale: { borderColor: line },
      timeScale: { borderColor: line, timeVisible: true, secondsVisible: false, tickMarkFormatter },
      crosshair: { mode: 0 },
      localization: { timeFormatter: crosshairTimeFormatter },
      // See disablePan's own prop comment — mouse wheel/drag/pinch pan-zoom
      // disabled entirely when the caller replaced free scrolling with its
      // own fixed-window + nav-arrows model. Crosshair hover (tooltips) is
      // a separate mechanism, unaffected by either setting.
      handleScroll: !disablePan,
      handleScale: !disablePan,
    });

    const series =
      mode === "candles"
        ? chart.addCandlestickSeries({
            upColor: long,
            downColor: short,
            borderVisible: false,
            wickUpColor: long,
            wickDownColor: short,
          })
        : chart.addAreaSeries({
            lineColor: accent,
            topColor: chartFill,
            bottomColor: "transparent",
            lineWidth: 2,
            priceLineVisible: false,
          });

    chartRef.current = chart;
    seriesRef.current = series;
    onChartApiRef.current?.(chart);

    // Every marker within HOVER_PX, nearest first — NOT just the single
    // closest one (see the tooltip state comment above for why).
    //
    // Markers sharing the same bar AND the same side (aboveBar/belowBar) are
    // NOT independently at their own trade price on screen — lightweight-
    // charts stacks them progressively further from the bar itself (see
    // MARKER_STACK_OFFSET_PX). Hit-testing against each marker's raw own
    // price (an earlier version of this function) only ever matched
    // whichever one happened to land closest to that shared price — every
    // OTHER marker in a busy same-day cluster was silently unreachable by
    // hover no matter where the cursor was, reported live as "выход
    // triangles have no tooltip" specifically on a dense cluster of
    // same-day exits. Grouping by (bar, side) and stacking the hit-test
    // targets the same way the library visually stacks the arrows fixes
    // this for every marker in the cluster, not just the first.
    function nearbyMarkers(param: MouseEventParams): PriceChartMarker[] {
      const activeSeries = seriesRef.current;
      if (!param.point || !activeSeries) return [];
      const point = param.point;

      const placed: { m: PriceChartMarker; x: number; below: boolean }[] = [];
      for (const m of markersRef.current) {
        // floor in candle mode — see timeToCoordinateNearestBar's own
        // comment: a bucket-START bar time (candles) needs "which bucket
        // does this timestamp belong to", not "which bucket time is
        // numerically closest".
        const x = timeToCoordinateNearestBar(chart, activeTimesRef.current, m.time as number, mode === "candles");
        if (x === null) continue;
        // Same side logic as the markers-effect's setMarkers() call below —
        // must match exactly, or a marker gets grouped/stacked on the wrong
        // side from how it's actually drawn.
        const entryBelow = m.direction === "LONG";
        const below = m.kind === "entry" ? entryBelow : !entryBelow;
        placed.push({ m, x, below });
      }

      const groups = new Map<string, typeof placed>();
      for (const p of placed) {
        const key = `${Math.round(p.x)}:${p.below ? "b" : "a"}`;
        const arr = groups.get(key);
        if (arr) arr.push(p);
        else groups.set(key, [p]);
      }

      const found: { m: PriceChartMarker; dist: number }[] = [];
      for (const [key, group] of groups) {
        // The group's FIRST marker (closest to the bar) anchors at its own
        // trade price — a reasonable stand-in for "the bar's own price"
        // (which isn't cheaply available for both series types here) since
        // a same-day exit's price is necessarily close to that day's bar.
        // Every subsequent marker in the group stacks away from THAT same
        // anchor, not its own price — matching the library's own behavior.
        const baseY = activeSeries.priceToCoordinate(group[0].m.price);
        if (baseY === null) continue;
        const below = key.endsWith(":b");
        group.forEach((p, idx) => {
          const y = baseY + idx * MARKER_STACK_OFFSET_PX * (below ? 1 : -1);
          const dist = Math.hypot(p.x - point.x, y - point.y);
          if (dist <= HOVER_PX) found.push({ m: p.m, dist });
        });
      }
      found.sort((a, b) => a.dist - b.dist);
      return found.map((f) => f.m);
    }

    const handleMove = (param: MouseEventParams) => {
      const found = nearbyMarkers(param);
      if (found.length === 0 || !param.point) {
        setTooltip(null);
        return;
      }
      setTooltip({ x: param.point.x, y: param.point.y, markers: found });
    };

    const handleClick = (param: MouseEventParams) => {
      const found = nearbyMarkers(param);
      if (found.length > 0) onMarkerClickRef.current?.(found[0].tradeId);
    };

    chart.subscribeCrosshairMove(handleMove);
    chart.subscribeClick(handleClick);

    // Day boundaries (00:00 MSK) drawn as brighter vertical lines than the
    // library's own --grid, so a day's worth of bars is visually
    // identifiable at a glance — user request: the plain grid was too
    // subtle in dark theme to tell where one day ends and the next starts.
    // Same "nearest actual bar, not continuous interpolation" reasoning as
    // nearbyMarkers/timeToCoordinateNearestBar above applies here too: bars
    // are spaced by INDEX, not by real elapsed time, so a linearly-
    // interpolated pixel position for an arbitrary midnight timestamp would
    // land in the wrong place across any real gap in market_metrics.
    // Deliberately `nearest`, NOT `floor` (unlike nearbyMarkers' own candle-
    // mode call) — a gridline has no "this instant belongs to bucket X"
    // ownership requirement the way a trade marker does, it only needs to
    // visually anchor near the closest existing bar, so nearest-by-distance
    // is the semantically correct choice here, not a shortcut. Concretely:
    // candle buckets are UTC-midnight-aligned (date_trunc('day', timestamp),
    // no explicit zone), 3h off from the MSK-midnight instant this function
    // is locating, so for a daily-bucketed range `nearest` lands within ~3h
    // of true midnight (picks the day-N bar, 3h ahead, over day-(N-1)'s, 21h
    // behind) — `floor` would instead land up to 21h early, in the middle of
    // the wrong day. Imperceptible either way at
    // any zoom level that shows day boundaries at all.
    function recomputeDayLines() {
      // Same try/catch reasoning as Overview.tsx's syncRange: a subscription
      // callback registered here can still fire once more while this exact
      // chart is mid-teardown (theme/mode/height switch tears down and
      // recreates via this same effect's own cleanup+rerun) — confirmed
      // live, an earlier version without this guard threw "Object is
      // disposed" straight out of the subscribeVisibleTimeRangeChange
      // callback on every theme toggle. Nothing to compute in that case.
      try {
        const range = chart.timeScale().getVisibleRange();
        if (!range || activeTimesRef.current.length === 0) {
          setDayLineX([]);
          return;
        }
        const xs: number[] = [];
        for (const b of mskDayBoundaries(range.from as number, range.to as number)) {
          const x = timeToCoordinateNearestBar(chart, activeTimesRef.current, b);
          if (x !== null) xs.push(x);
        }
        setDayLineX(xs);
      } catch {
        // chart mid-teardown
      }
    }
    recomputeDayLinesRef.current = recomputeDayLines;
    chart.timeScale().subscribeVisibleTimeRangeChange(recomputeDayLines);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) chart.applyOptions({ width: entry.contentRect.width });
      recomputeDayLines();
    });
    resizeObserver.observe(container);

    return () => {
      chart.unsubscribeCrosshairMove(handleMove);
      chart.unsubscribeClick(handleClick);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(recomputeDayLines);
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      if (candleRefitTimeoutRef.current !== null) {
        clearTimeout(candleRefitTimeoutRef.current);
        candleRefitTimeoutRef.current = null;
      }
      setTooltip(null);
      setDayLineX([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, height, mode]);

  // Data updates — always fit to show the FULL current dataset edge-to-
  // edge. There used to be a real distinction between "the user just
  // switched range/period" (should re-fit) and "this is just a 5s poll
  // refresh or a load-more append on the SAME range" (should preserve
  // whatever the user was panned/zoomed to) — that distinction no longer
  // exists now that disablePan removes free panning/zooming entirely (see
  // its own prop comment): there's nothing left to preserve, the visible
  // range should always just be "everything currently loaded".
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    if (mode === "candles") {
      (series as ISeriesApi<"Candlestick">).setData(candles);
      try {
        chart.timeScale().fitContent();
      } catch {
        // chart mid-teardown
      }
      // Candlestick series' setData() has its own delayed internal "snap to
      // the latest bars" behavior that silently overwrites a synchronous
      // fitContent() call right after it — confirmed live via timestamped
      // logging: fitContent() reads back correctly at +0ms, then the
      // library's OWN internal update overwrites it by +100ms to the last
      // ~2 bars. A debounced corrective re-fit, scheduled to run AFTER that
      // internal snap, fights it back — debounced so a fast cascade of
      // updates only runs the LAST scheduled correction.
      if (candleRefitTimeoutRef.current !== null) clearTimeout(candleRefitTimeoutRef.current);
      candleRefitTimeoutRef.current = setTimeout(() => {
        candleRefitTimeoutRef.current = null;
        try {
          chart.timeScale().fitContent();
        } catch {
          // chart mid-teardown
        }
      }, 200);
      recomputeDayLinesRef.current();
      return;
    }

    (series as ISeriesApi<"Area">).setData(data);
    try {
      chart.timeScale().fitContent();
    } catch {
      // chart mid-teardown
    }
    recomputeDayLinesRef.current();
  }, [mode, data, candles]);

  // Markers (trade entries/exits) — visual placement only, see the
  // hover/click hit-testing set up above.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    const long = resolveCssVar("--long");
    const short = resolveCssVar("--short");
    series.setMarkers(
      markers.map((m) => {
        // Entry arrows point in the trade's direction; exit arrows point
        // the opposite way (closing motion) so entries/exits are visually
        // distinguishable at a glance, not just via the text label.
        const entryBelow = m.direction === "LONG";
        const below = m.kind === "entry" ? entryBelow : !entryBelow;
        return {
          time: m.time,
          position: below ? "belowBar" : "aboveBar",
          color: m.direction === "LONG" ? long : short,
          shape: below ? "arrowUp" : "arrowDown",
          // Bot label prefix — user request: with several bots trading the
          // same market, a bare "вход"/"выход" on a dense cluster doesn't
          // say WHICH bot without also opening the hover tooltip. botLabel
          // is only null for a trade whose bot_instance_id doesn't match
          // the expected "{type}-{digits}" shape (see serializers.py's
          // bot_label()) — omit the prefix rather than show a stray space.
          text: `${m.botLabel ? `${m.botLabel} ` : ""}${m.kind === "entry" ? "вход" : "выход"}`,
        };
      }),
    );
    // `mode` is a dependency, not just `markers`: switching line/candles
    // creates a NEW series object (see the create/destroy effect above) —
    // without re-running this effect on that switch, setMarkers() would
    // still be pointed at the just-destroyed series, and the new one would
    // show no markers at all until the `markers` prop happened to change
    // for an unrelated reason. Confirmed live: markers vanished on toggling
    // to candles before this fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, mode]);

  // Horizontal SL/TP/entry price lines — same "new series on mode switch"
  // reasoning as the markers effect above.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    const accent = resolveCssVar("--accent");
    const long = resolveCssVar("--long");
    const short = resolveCssVar("--short");
    const colorFor = (kind: PriceChartLevel["kind"]) => (kind === "entry" ? accent : kind === "tp" ? long : short);
    const priceLines = levels.map((lv) =>
      series.createPriceLine({
        price: lv.price,
        color: colorFor(lv.kind),
        lineWidth: 1,
        lineStyle: lv.kind === "entry" ? 0 : 2, // solid for entry, dashed for SL/TP
        axisLabelVisible: true,
        title: lv.title,
      }),
    );
    return () => {
      priceLines.forEach((pl) => series.removePriceLine(pl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels, mode]);

  return (
    <div style={{ position: "relative" }} data-testid="price-chart">
      <div ref={containerRef} style={{ width: "100%", height }} />
      {dayLineX.map((x) => (
        <div
          key={x}
          style={{
            position: "absolute",
            left: x,
            top: 0,
            bottom: 0,
            width: 1,
            background: "var(--line2)",
            opacity: 0.75,
            pointerEvents: "none",
          }}
        />
      ))}
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            transform: "translate(-50%, -120%)",
            pointerEvents: "none",
            padding: "9px 11px",
            borderRadius: 10,
            background: "var(--surface3)",
            border: "1px solid var(--line2)",
            boxShadow: "var(--pop)",
            minWidth: 210,
            // Must clear every other sticky/positioned layer on the page —
            // NavSidebar (40) and AppShell's sticky header (30) both sit
            // above the chart's own stacking context, so anything lower
            // than that gets hidden under them near the top/left edges of
            // the chart where hover is most common.
            zIndex: 50,
          }}
        >
          {tooltip.markers.length === 1 ? (
            <MarkerDetail m={tooltip.markers[0]} />
          ) : (
            <>
              {/* market_metrics is hourly, so several trades' markers can land
                  on the same bar (see the tooltip state comment above) — list
                  every one within range instead of hiding all but the
                  cursor-nearest, which made the others unreachable by hover. */}
              <div style={{ font: "700 10.5px 'Golos Text',sans-serif", letterSpacing: ".05em", color: "var(--fg2)", textTransform: "uppercase" }}>
                {tooltip.markers.length} операции рядом
              </div>
              {tooltip.markers.map((m, i) => (
                <div
                  key={`${m.tradeId}-${m.kind}`}
                  style={i === 0 ? { marginTop: 6 } : { marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)" }}
                >
                  <MarkerSummaryRow m={m} />
                </div>
              ))}
              <div
                style={{
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: "1px solid var(--line)",
                  font: "400 10.5px 'JetBrains Mono',monospace",
                  color: "var(--fg3)",
                }}
              >
                клик — детали ближайшей к курсору
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, color, bold }: { label: string; value: string; color?: string; bold?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 4, font: "400 11.5px 'JetBrains Mono',monospace" }}>
      <span style={{ color: "var(--fg3)" }}>{label}</span>
      <span style={{ color: color ?? "var(--fg)", fontWeight: bold ? 700 : 500, whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

// Full single-trade detail — the tooltip's original layout, unchanged, now
// reused for the single-marker case (the common one) and factored out so
// the multi-marker case (PriceChart's tooltip render) can list several of
// these without duplicating the markup.
function MarkerDetail({ m }: { m: PriceChartMarker }) {
  const dirColor = m.direction === "LONG" ? "var(--long)" : "var(--short)";
  const dirSoft = m.direction === "LONG" ? "var(--longsoft)" : "var(--shortsoft)";
  // Raw price delta (exit - entry), independent of P&L (which also factors
  // in size/fees) — the "разница между ними" the tooltip needs alongside
  // P&L, not instead of it.
  const delta = m.exitPrice !== null ? m.exitPrice - m.entryPrice : null;
  const deltaPct = delta !== null && m.entryPrice ? (delta / m.entryPrice) * 100 : null;
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {m.botLabel && (
          <span
            style={{
              font: "700 9.5px 'JetBrains Mono',monospace",
              padding: "1px 5px",
              borderRadius: 5,
              background: "var(--surface2)",
              color: "var(--fg3)",
            }}
          >
            {m.botLabel}
          </span>
        )}
        <span
          style={{
            font: "700 10.5px 'Golos Text',sans-serif",
            letterSpacing: ".05em",
            padding: "1px 6px",
            borderRadius: 5,
            background: dirSoft,
            color: dirColor,
          }}
        >
          {m.direction}
        </span>
        <span style={{ font: "400 11px 'Golos Text',sans-serif", color: "var(--fg2)" }}>
          {m.status === "OPEN" ? "открыта" : "закрыта"}
        </span>
      </div>

      <Row label="Вход" value={formatMoney(m.entryPrice)} />
      <Row label="Время входа" value={formatTime(new Date(m.entryTime * 1000).toISOString(), true)} />
      <Row label="Выход" value={m.exitPrice !== null ? formatMoney(m.exitPrice) : "—"} />
      <Row label="Время выхода" value={m.exitTime !== null ? formatTime(new Date(m.exitTime * 1000).toISOString(), true) : "—"} />
      <Row
        label="Разница"
        value={delta !== null ? `${delta >= 0 ? "+" : "−"}${formatMoney(Math.abs(delta))} (${deltaPct !== null ? formatPct(deltaPct) : "—"})` : "—"}
        color={delta !== null ? (delta >= 0 ? "var(--long)" : "var(--short)") : undefined}
      />
      <Row label="Размер" value={`${m.size} BTC`} />
      <Row
        label="P&L"
        value={m.pnl !== null ? formatPnl(m.pnl) : "—"}
        color={m.pnl !== null ? (parseFloat(m.pnl) >= 0 ? "var(--long)" : "var(--short)") : undefined}
        bold
      />

      <div
        style={{
          marginTop: 6,
          paddingTop: 6,
          borderTop: "1px solid var(--line)",
          font: "400 10.5px 'JetBrains Mono',monospace",
          color: "var(--fg3)",
        }}
      >
        {formatTime(new Date(m.time * 1000).toISOString(), true)} · {m.kind === "entry" ? "вход" : "выход"}
        <br />
        {formatTradeId(m.tradeId)} · клик — детали
      </div>
    </>
  );
}

// Condensed one-trade-event summary for the multi-marker tooltip — full
// MarkerDetail per item would make a 3-4-marker cluster's tooltip
// unreasonably tall.
function MarkerSummaryRow({ m }: { m: PriceChartMarker }) {
  const dirColor = m.direction === "LONG" ? "var(--long)" : "var(--short)";
  const dirSoft = m.direction === "LONG" ? "var(--longsoft)" : "var(--shortsoft)";
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {m.botLabel && (
          <span
            style={{
              font: "700 9.5px 'JetBrains Mono',monospace",
              padding: "1px 5px",
              borderRadius: 5,
              background: "var(--surface2)",
              color: "var(--fg3)",
            }}
          >
            {m.botLabel}
          </span>
        )}
        <span
          style={{
            font: "700 10.5px 'Golos Text',sans-serif",
            letterSpacing: ".05em",
            padding: "1px 6px",
            borderRadius: 5,
            background: dirSoft,
            color: dirColor,
          }}
        >
          {m.direction}
        </span>
        <span style={{ font: "600 11px 'Golos Text',sans-serif" }}>{m.kind === "entry" ? "вход" : "выход"}</span>
        <span style={{ flex: 1 }} />
        <span style={{ font: "600 11.5px 'JetBrains Mono',monospace" }}>{formatMoney(m.price)}</span>
      </div>
      <Row label="Время" value={formatTime(new Date(m.time * 1000).toISOString(), true)} />
      <Row label="Сделка" value={formatTradeId(m.tradeId)} />
    </>
  );
}
